import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../components/ui/command";
import {
  ArrowLeft, FileDown, MessageCircle, AlertTriangle, Printer,
  BarChart3, Users, Building2, Calendar, UserCircle, ChevronsUpDown, Check,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STATUSES = ["Present", "Late", "Absent", "Medical", "MC", "MIA", "Off", "NG", "NG IN"];

const REPORT_KINDS = [
  { key: "full",    label: "Full attendance",  icon: BarChart3, desc: "Every worker, every day." },
  { key: "mia",     label: "MIA report",       icon: AlertTriangle, desc: "Workers gone missing." },
  { key: "absent",  label: "Absent report",    icon: AlertTriangle, desc: "Absence counts + streaks." },
  { key: "late",    label: "Late report",      icon: AlertTriangle, desc: "Lateness minutes." },
  { key: "perfect", label: "Perfect attendance", icon: Users, desc: "Zero Absent/MIA/Late." },
  { key: "company", label: "Company summary",  icon: Building2, desc: "Rates + totals per factory." },
  { key: "worker",  label: "Individual worker", icon: UserCircle, desc: "Full history for one worker." },
];

function today() { return new Date().toISOString().slice(0, 10); }
function monthAgo() {
  const d = new Date(); d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

export default function Reports() {
  const [kind, setKind] = useState("full");
  const [filters, setFilters] = useState({
    date_from: monthAgo(), date_to: today(), company_id: "", status: "", worker_id: "",
  });
  const [companies, setCompanies] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/roster/companies").then(({ data }) => setCompanies(data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (kind !== "worker") return;
    const url = filters.company_id
      ? `/roster/companies/${filters.company_id}/workers`
      : `/roster/workers`;
    api.get(url)
      .then(({ data }) => setWorkers(data.workers || []))
      .catch(() => setWorkers([]));
  }, [kind, filters.company_id]);

  const run = useCallback(async () => {
    if (kind === "worker" && !filters.worker_id) {
      toast.error("Pick a worker"); return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/roster/reports/${kind}`, {
        params: {
          date_from: filters.date_from, date_to: filters.date_to,
          company_id: filters.company_id || undefined,
          status: filters.status || undefined,
          worker_id: filters.worker_id || undefined,
        },
      });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  }, [kind, filters]);

  const shareWa = async () => {
    if (!data) { toast.error("Run a report first"); return; }
    const text = buildWaMessage(kind, filters, data);
    // wa.me mangles 4-byte emojis — use clipboard paste flow instead.
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied. Open WhatsApp chat and paste (Ctrl+V).",
        { duration: 6000 });
    } catch {
      toast.error("Couldn't copy the report.");
      return;
    }
    window.open("https://web.whatsapp.com/", "_blank", "noopener");
  };

  const currentKind = REPORT_KINDS.find((r) => r.key === kind);

  return (
    <Layout title="Kedatangan · Reports">
      <style>{`
        @media print {
          body, html { background: #fff !important; color: #000 !important; }
          .no-print { display: none !important; }
          .print-container { background: #fff !important; color: #000 !important; }
          .print-container * { color: #000 !important; border-color: #ddd !important; }
          .print-container table { width: 100%; border-collapse: collapse; }
          .print-container th, .print-container td { border: 1px solid #ddd; padding: 4px 6px; font-size: 11px; }
          .print-container thead { background: #f4f4f4 !important; -webkit-print-color-adjust: exact; }
          .streak-warn { color: #b91c1c !important; }
        }
      `}</style>

      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap no-print">
          <Link to="/kedatangan">
            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-200">
              <ArrowLeft className="w-4 h-4 mr-1" /> Kedatangan
            </Button>
          </Link>
          <h1 className="text-xl font-semibold tracking-tight">Reports</h1>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => window.print()} disabled={!data}
            className="border-slate-700 hover:border-blue-500" data-testid="print-report">
            <Printer className="w-4 h-4 mr-1.5" /> Print / PDF
          </Button>
          <Button variant="outline" onClick={shareWa} disabled={!data}
            className="border-slate-700 hover:border-emerald-500" data-testid="wa-report">
            <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
          </Button>
        </div>

        {/* Kind picker */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 no-print">
          {REPORT_KINDS.map((k) => {
            const Icon = k.icon;
            const active = k.key === kind;
            return (
              <button key={k.key}
                onClick={() => { setKind(k.key); setData(null); }}
                className={`rounded-lg border p-3 text-left transition ${active
                  ? "border-blue-500/60 bg-blue-500/10" : "border-slate-800 hover:border-slate-600 surface-1"}`}
                data-testid={`kind-${k.key}`}>
                <Icon className={`w-4 h-4 mb-1.5 ${active ? "text-blue-300" : "text-slate-400"}`} />
                <div className="text-sm font-medium">{k.label}</div>
                <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">{k.desc}</div>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="rounded-lg border hairline surface-1 p-3 grid grid-cols-1 md:grid-cols-5 gap-3 items-end no-print">
          <div>
            <Label className="text-xs text-slate-400">Date from</Label>
            <Input type="date" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1 num" data-testid="date-from" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Date to</Label>
            <Input type="date" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1 num" data-testid="date-to" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Company</Label>
            <Select value={filters.company_id || "all"}
              onValueChange={(v) => setFilters({ ...filters, company_id: v === "all" ? "" : v, worker_id: "" })}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="filter-company">
                <SelectValue placeholder="All companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {kind === "full" && (
            <div>
              <Label className="text-xs text-slate-400">Status</Label>
              <Select value={filters.status || "all"}
                onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1">
                  <SelectValue placeholder="Any status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any status</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {kind === "worker" && (
            <div>
              <Label className="text-xs text-slate-400">Worker</Label>
              <WorkerCombobox
                workers={workers}
                value={filters.worker_id}
                showCompany={!filters.company_id}
                onChange={(v) => setFilters({ ...filters, worker_id: v })}
              />
            </div>
          )}
          <Button onClick={run} disabled={loading}
            className="bg-blue-600 hover:bg-blue-500 h-9" data-testid="run-report">
            {loading ? "Loading…" : "Run report"}
          </Button>
        </div>

        {/* Rendered report */}
        <div className="print-container rounded-lg border hairline surface-1 p-4 min-h-[200px]"
          data-testid="report-body">
          {!data && <div className="text-sm text-slate-500 text-center py-10">
            Pick a report type, date range and press <b>Run report</b>.
          </div>}
          {data && (
            <>
              <ReportHeader data={data} companies={companies} kindLabel={currentKind.label} />
              {kind === "full"    && <FullReport data={data} />}
              {kind === "mia"     && <StreakReport data={data} tone="red" heading="MIA report" />}
              {kind === "absent"  && <StreakReport data={data} tone="amber" heading="Absent report" />}
              {kind === "late"    && <LateReport data={data} />}
              {kind === "perfect" && <PerfectReport data={data} />}
              {kind === "company" && <CompanyReport data={data} />}
              {kind === "worker"  && <WorkerReport data={data} />}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

function ReportHeader({ data, companies, kindLabel }) {
  const coName = data.company_filter === "all"
    ? "All companies"
    : (companies.find((c) => c.id === data.company_filter)?.name || "—");
  return (
    <div className="mb-3 pb-3 border-b hairline">
      <div className="text-xl font-semibold">{kindLabel}</div>
      <div className="text-xs text-slate-500 num mt-1">
        {data.date_from} → {data.date_to} · {coName}
        {data.status_filter && data.status_filter !== "all" ? ` · ${data.status_filter}` : ""}
      </div>
    </div>
  );
}

function dash(v) { return v == null || v === "" ? "—" : v; }

function FullReport({ data }) {
  const t = data.totals || {};
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="full-table">
          <thead>
            <tr className="text-left text-xs text-slate-400 uppercase num border-b hairline">
              <th className="py-1.5 px-2">#</th>
              <th className="px-2">Worker</th>
              <th className="px-2">Company</th>
              <th className="px-2">Date</th>
              <th className="px-2">Status</th>
              <th className="px-2">Clock in</th>
              <th className="px-2">Clock out</th>
              <th className="px-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).map((r, i) => (
              <tr key={i} className="border-b border-slate-900/80">
                <td className="py-1 px-2 num text-slate-500">{i + 1}</td>
                <td className="px-2">{r.worker_name}</td>
                <td className="px-2 text-slate-400">{r.company_name}</td>
                <td className="px-2 num">{r.date}</td>
                <td className="px-2">{dash(r.status)}</td>
                <td className="px-2 num">{dash(r.check_in)}</td>
                <td className="px-2 num">{dash(r.check_out)}</td>
                <td className="px-2 text-xs text-slate-400">{dash(r.notes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 pt-3 border-t hairline grid grid-cols-3 md:grid-cols-9 gap-2 text-xs">
        {Object.entries(t).map(([k, v]) => (
          <div key={k} className="rounded px-2 py-1 surface-2 border hairline num">
            <div className="text-[10px] text-slate-500 uppercase">{k}</div>
            <div className="text-lg font-semibold">{v}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function StreakReport({ data, tone, heading }) {
  const warnColor = tone === "red" ? "text-red-400" : "text-amber-300";
  return (
    <div className="overflow-x-auto">
      {(data.rows || []).length === 0 && (
        <div className="text-sm text-slate-500 text-center py-6">Nothing to report for this range.</div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-400 uppercase num border-b hairline">
            <th className="py-1.5 px-2">Worker</th>
            <th className="px-2">Company</th>
            <th className="px-2">Total</th>
            <th className="px-2">Max streak</th>
            <th className="px-2">Dates</th>
          </tr>
        </thead>
        <tbody>
          {(data.rows || []).map((r) => (
            <tr key={r.worker_id} className="border-b border-slate-900/80 align-top">
              <td className="py-1 px-2">{r.worker_name}</td>
              <td className="px-2 text-slate-400">{r.company_name}</td>
              <td className="px-2 num">{r.count}</td>
              <td className={`px-2 num ${r.streak_warning ? `streak-warn ${warnColor}` : ""}`}>
                {r.max_streak}{r.streak_warning ? ` ⚠ Consecutive ${heading.split(" ")[0]}` : ""}
              </td>
              <td className="px-2 num text-xs text-slate-400">{(r.dates || []).join(", ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LateReport({ data }) {
  return (
    <div className="overflow-x-auto">
      {(data.rows || []).length === 0 && (
        <div className="text-sm text-slate-500 text-center py-6">No late records in this range.</div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-400 uppercase num border-b hairline">
            <th className="py-1.5 px-2">Worker</th>
            <th className="px-2">Company</th>
            <th className="px-2">Total late</th>
            <th className="px-2">Instances (date · in · min late)</th>
          </tr>
        </thead>
        <tbody>
          {(data.rows || []).map((r) => (
            <tr key={r.worker_id} className="border-b border-slate-900/80 align-top">
              <td className="py-1 px-2">{r.worker_name}</td>
              <td className="px-2 text-slate-400">{r.company_name}</td>
              <td className="px-2 num">{r.count}</td>
              <td className="px-2 text-xs text-slate-400">
                {(r.late_instances || []).map((i, idx) => (
                  <span key={idx} className="num mr-2">
                    {i.date}{i.check_in ? ` ${i.check_in}` : ""}{i.minutes != null ? ` · ${i.minutes}m` : ""}
                    {idx < r.late_instances.length - 1 ? " ·" : ""}
                  </span>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PerfectReport({ data }) {
  return (
    <>
      <div className="text-xs text-slate-500 num mb-2">{data.days_in_range} days in range</div>
      {(data.rows || []).length === 0 && (
        <div className="text-sm text-slate-500 text-center py-6">No perfect attendees this range.</div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-400 uppercase num border-b hairline">
            <th className="py-1.5 px-2">Worker</th>
            <th className="px-2">Company</th>
            <th className="px-2">Present</th>
            <th className="px-2">NG</th>
            <th className="px-2">NG IN</th>
            <th className="px-2">Rate</th>
          </tr>
        </thead>
        <tbody>
          {(data.rows || []).map((r) => (
            <tr key={r.worker_id} className="border-b border-emerald-900/50 bg-emerald-500/5">
              <td className="py-1 px-2">{r.worker_name}</td>
              <td className="px-2 text-slate-400">{r.company_name}</td>
              <td className="px-2 num">{r.present_days}</td>
              <td className="px-2 num">{r.ng_days}</td>
              <td className="px-2 num">{r.ng_in_days}</td>
              <td className="px-2 num text-emerald-300">{r.attendance_rate}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function CompanyReport({ data }) {
  const cols = ["Present", "Late", "Absent", "Medical", "MC", "MIA", "NG", "NG IN"];
  return (
    <div className="overflow-x-auto">
      <div className="text-xs text-slate-500 num mb-2">{data.days_in_range} days in range</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-400 uppercase num border-b hairline">
            <th className="py-1.5 px-2">Company</th>
            <th className="px-2">Workers</th>
            {cols.map((c) => <th key={c} className="px-2">{c}</th>)}
            <th className="px-2">Rate</th>
          </tr>
        </thead>
        <tbody>
          {(data.rows || []).map((r) => (
            <tr key={r.company_id} className="border-b border-slate-900/80">
              <td className="py-1 px-2">{r.company_name}</td>
              <td className="px-2 num">{r.total_workers}</td>
              {cols.map((c) => <td key={c} className="px-2 num">{r.totals[c] || 0}</td>)}
              <td className="px-2 num text-emerald-300">{r.attendance_rate}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WorkerReport({ data }) {
  const c = data.counts || {};
  const cols = ["Present", "Late", "Absent", "Medical", "MC", "MIA", "Off", "NG", "NG IN"];
  return (
    <>
      <div className="mb-3">
        <div className="text-lg font-medium">{data.worker?.name}</div>
        <div className="text-xs text-slate-500">{data.worker?.company_name}</div>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-9 gap-2 text-xs mb-4">
        {cols.map((k) => (
          <div key={k} className="rounded px-2 py-1 surface-2 border hairline num">
            <div className="text-[10px] text-slate-500 uppercase">{k}</div>
            <div className="text-lg font-semibold">{c[k] || 0}</div>
          </div>
        ))}
        <div className="rounded px-2 py-1 border border-emerald-500/40 bg-emerald-500/10 num">
          <div className="text-[10px] uppercase text-emerald-400">Rate</div>
          <div className="text-lg font-semibold text-emerald-300">{data.attendance_rate}%</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-400 uppercase num border-b hairline">
              <th className="py-1.5 px-2">Date</th>
              <th className="px-2">Status</th>
              <th className="px-2">Clock in</th>
              <th className="px-2">Clock out</th>
              <th className="px-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {(data.daily || []).map((d) => (
              <tr key={d.date} className="border-b border-slate-900/80">
                <td className="py-1 px-2 num">{d.date}</td>
                <td className="px-2">{dash(d.status)}</td>
                <td className="px-2 num">{dash(d.check_in)}</td>
                <td className="px-2 num">{dash(d.check_out)}</td>
                <td className="px-2 text-xs text-slate-400">{dash(d.notes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function WorkerCombobox({ workers, value, showCompany, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = workers.find((w) => w.id === value);
  const label = selected
    ? `${selected.name}${showCompany && selected.company_name ? ` · ${selected.company_name}` : ""}`
    : (workers.length ? "Pick a worker" : "Loading…");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="filter-worker"
          className="w-full justify-between mt-1 bg-[#0a0e1a] border-slate-800 font-normal text-slate-200 hover:bg-[#0a0e1a]/80 hover:text-slate-100"
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        avoidCollisions={false}
        className="p-0 w-[--radix-popover-trigger-width] min-w-[280px] bg-[#0f1626] border-slate-800"
      >
        <Command className="bg-transparent">
          <CommandInput
            placeholder="Search worker…"
            data-testid="worker-search-input"
            className="h-9"
          />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>No worker found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none"
                onSelect={() => { onChange(""); setOpen(false); }}
              >
                <Check className={`mr-2 h-4 w-4 ${!value ? "opacity-100" : "opacity-0"}`} />
                <span className="text-slate-400">— Clear</span>
              </CommandItem>
              {workers.map((w) => {
                const display = `${w.name}${showCompany && w.company_name ? ` · ${w.company_name}` : ""}`;
                return (
                  <CommandItem
                    key={w.id}
                    value={`${w.name} ${w.company_name || ""} ${w.code || ""}`}
                    onSelect={() => { onChange(w.id); setOpen(false); }}
                  >
                    <Check className={`mr-2 h-4 w-4 ${value === w.id ? "opacity-100" : "opacity-0"}`} />
                    <span className="truncate">{display}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function buildWaMessage(kind, filters, data) {
  const lines = [];
  lines.push(`*Kedatangan · ${REPORT_KINDS.find((r) => r.key === kind).label}*`);
  lines.push(`${filters.date_from} → ${filters.date_to}`);
  if (kind === "company" && data.rows) {
    data.rows.forEach((r) => {
      lines.push(`• ${r.company_name}: ${r.total_workers} workers · ${r.attendance_rate}% attended`);
    });
  } else if (kind === "mia" || kind === "absent" || kind === "late") {
    (data.rows || []).slice(0, 15).forEach((r) => {
      const warn = r.streak_warning ? " ⚠" : "";
      lines.push(`• ${r.worker_name} (${r.company_name}) — ${r.count}${warn}`);
    });
  } else if (kind === "perfect") {
    (data.rows || []).slice(0, 20).forEach((r) => {
      lines.push(`• ✅ ${r.worker_name} (${r.company_name}) — ${r.attendance_rate}%`);
    });
  } else if (kind === "worker") {
    const c = data.counts || {};
    lines.push(`${data.worker?.name} — ${data.worker?.company_name}`);
    lines.push(`Present: ${c.Present || 0} · Late: ${c.Late || 0} · Absent: ${c.Absent || 0} · MIA: ${c.MIA || 0}`);
    lines.push(`Attendance rate: ${data.attendance_rate}%`);
  } else if (kind === "full") {
    const t = data.totals || {};
    lines.push(Object.entries(t).map(([k, v]) => `${k}:${v}`).join(" · "));
    lines.push(`Rows: ${(data.rows || []).length}`);
  }
  lines.push("— Mercy Management System");
  return lines.join("\n");
}
